import { ReactElement, ReactNode } from 'react';

import styled from 'styled-components';

import { IconProps } from '@components/icons';
import { IconTravellerStatusCompleted } from '@components/icons/traveller-status-completed';
import { IconTravellerStatusInProgress } from '@components/icons/traveller-status-in-progress';
import { IconTravellerStatusNotStarted } from '@components/icons/traveller-status-not-started';
import { IconTravellerStatusPaused } from '@components/icons/traveller-status-paused';
import { Color, theme } from '@styles/theme';
import { getSum } from '@utils/number-arrays';

export type PiechartSection = {
  color: Color;
  value: number;
};

type StatusType = 'not_started' | 'paused' | 'in_progress' | 'completed';

export type PiechartCircleProps = {
  size?: number;
  sections: PiechartSection[];
  children?: ReactNode;
};

const getConicGradient = (sections: PiechartCircleProps['sections']) => {
  const valueSum = getSum(sections.map((section) => section.value));

  let currentRatio = 0;
  const mappedSections: string[] = [];
  sections.forEach((section) => {
    const endRatio = section.value / valueSum + currentRatio;
    mappedSections.push(`${theme.color[section.color]} ${currentRatio * 100}% ${endRatio * 100}%`);
    currentRatio = endRatio;
  });

  return mappedSections.join(', ');
};

type StatusMapType = {
  [key in StatusType]: ReactElement<IconProps>;
};

export const PiechartCircle = ({ size = 7, sections, children }: PiechartCircleProps) => {
  return (
    <Chart size={size} sections={sections}>
      {children}
    </Chart>
  );
};

export type StatusPiechartCircleProps = PiechartCircleProps & {
  status?: StatusType;
};

export const StatusPiechartCircle = ({ size = 7, sections, status }: StatusPiechartCircleProps) => {
  const iconSize = (size / 2) * 16;

  const statusMap: StatusMapType = {
    not_started: <IconTravellerStatusNotStarted size={iconSize} />,
    paused: <IconTravellerStatusPaused size={iconSize} />,
    in_progress: <IconTravellerStatusInProgress size={iconSize} />,
    completed: <IconTravellerStatusCompleted size={iconSize} />,
  };

  return (
    <div style={{ position: 'relative', height: 'fit-content', width: 'fit-content' }}>
      {status && <StatusContainer>{statusMap[status]}</StatusContainer>}
      <PiechartCircle size={size} sections={sections}></PiechartCircle>
    </div>
  );
};

const maskImage = 'radial-gradient(circle, rgba(0, 0, 0, 0) 48%, rgba(0, 0, 0, 1) 49%)';

const Chart = styled.div<PiechartCircleProps>(({ size, sections }) => ({
  width: `${size}rem`,
  height: `${size}rem`,
  backgroundColor: theme.color.blueTrans10,
  borderRadius: '50%',
  background: `conic-gradient(${getConicGradient(sections)})`,
  maskImage,
  WebkitMaskImage: maskImage,
}));

const StatusContainer = styled.div({
  position: 'absolute',
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});
